import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Plumbing as PlumbingIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { initializeDB } from '../../../config/db';
import { PIPE_HYDRO_TEST_STORE } from '../../../config/constants';

export default function PipingForms() {
    const navigate = useNavigate();
    const [formsCount, setFormsCount] = useState(0);

    useEffect(() => {
        loadFormsCount();
    }, []);

    const loadFormsCount = async () => {
        try {
            const db = await initializeDB();
            if (db.objectStoreNames.contains(PIPE_HYDRO_TEST_STORE)) {
                const tx = db.transaction(PIPE_HYDRO_TEST_STORE, 'readonly');
                const store = tx.objectStore(PIPE_HYDRO_TEST_STORE);
                const allForms = await store.getAll();
                setFormsCount(allForms?.length || 0);
            }
        } catch (e) {
            console.error('Error loading forms count:', e);
        }
    };

    return (
        <Box sx={{
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton onClick={() => navigate('/main')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" fontWeight={600}>Piping Forms</Typography>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ p: 1.5 }}>
                <Grid container spacing={2}>
                    {/* Pipe Hydro Test Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/pipe-hydro-test/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Pipe Hydro Test
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Hydrostatic testing
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    {formsCount > 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            {formsCount} active form{formsCount !== 1 ? 's' : ''}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                                            No active forms
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Vessel Hydro Test Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/vessel-hydro-test/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Vessel Hydro Test
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Vessel testing form
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Service Leak Test Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/service-leak-test/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Service Leak Test
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Leak testing report
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Weld Map Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/weld-map/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Weld Map
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Weld inspection tracking
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Pneumatic Test Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/pneumatic-test/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Pneumatic Test
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Pneumatic pressure testing
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Torque Map Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/torque-map/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Torque Map
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Torque inspection tracking
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Gas Line Inspection Card */}
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Card
                            onClick={() => navigate('/piping/forms/gas-line-inspection/list')}
                            sx={{
                                minHeight: 140,
                                cursor: 'pointer',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: 3,
                                    borderColor: '#00acc1',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                }
                            }}
                        >
                            <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#00acc1',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        flexShrink: 0
                                    }}>
                                        <PlumbingIcon sx={{ fontSize: 24, color: 'white' }} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                lineHeight: 1.2,
                                                color: '#333'
                                            }}
                                        >
                                            Gas Line Inspection
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            Visual inspection report
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status section */}
                                <Box sx={{ mt: 'auto' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                        New form
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>

                    {/* Future forms can be added here as additional Grid items */}
                </Grid>
            </Box>
        </Box>
    );
}
